import React from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import { useEffect, useState } from 'react';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap'; // Adicionei Form, Row, Col
import { useNavigate } from 'react-router-dom';
import Spinner from '../../shared/spinner';
import { Comunicado } from '../../shared/model/comunicado';
import { format } from 'date-fns';
import { faEye, faCheck, faFilter, faRefresh, faFileExcel } from '@fortawesome/free-solid-svg-icons'; // Adicionei ícones faltantes
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ComunicadoService from '../../services/comunicadoService';
import ComunicadoConfirmacaoService from '../../services/comunicadoConfirmacaoService';
import RelatorioService from '../../services/relatorioService';
import { useVerificaPermissao } from '../../shared/useVerificaPermissao';

// Interface para os dados de confirmação
interface ConfirmacaoLeitura {
  id: number;
  tipoComunicado: string;
  nomeComunicado: string;
  login: string;
  nome: string;
  dtConfirmacao: string;
  promotora?: string;
  origem?: string;
}

// Interface para tipos de comunicado
interface TipoComunicado {
  id: string;
  descricao: string;
}

function ComunicadosComponent() {
  useVerificaPermissao();
  const [loading, setLoading] = useState(true);
  const [comunicados, setComunicados] = useState<Comunicado[]>([]);
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();

  // Estados para filtros
  const [filtros, setFiltros] = useState(false);
  const [nomeComunicadoFiltro, setNomeComunicadoFiltro] = useState('');
  const [loginFiltro, setLoginFiltro] = useState('');
  const [tipoComunicadoFiltro, setTipoComunicadoFiltro] = useState('');
  const [dataInicioFiltro, setDataInicioFiltro] = useState<Date | undefined>();
  const [dataFimFiltro, setDataFimFiltro] = useState<Date | undefined>();
  const [tiposComunicados, setTiposComunicados] = useState<TipoComunicado[]>([]);

  // Estados para o modal de confirmação
  const [showModal, setShowModal] = useState(false);
  const [confirmacoes, setConfirmacoes] = useState<ConfirmacaoLeitura[]>([]);
  const [loadingConfirmacoes, setLoadingConfirmacoes] = useState(false);
  const [comunicadoSelecionado, setComunicadoSelecionado] = useState<Comunicado | null>(null);

  const [paginaAtual, setPaginaAtual] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const tamanhoPagina = 10;

  // Para paginação do modal de confirmações
  const [paginaConfirmacoesAtual, setPaginaConfirmacoesAtual] = useState(0);
  const [totalPaginasConfirmacoes, setTotalPaginasConfirmacoes] = useState(0);

  const comunicadoService: ComunicadoService = new ComunicadoService();
  const comunicadoConfirmacaoService: ComunicadoConfirmacaoService = new ComunicadoConfirmacaoService();
  const relatorioService: RelatorioService = new RelatorioService();

  // Carregar tipos de comunicado para o filtro
  useEffect(() => {
    const carregarTiposComunicado = async () => {
      try {
        const response = await comunicadoService.listarTodosTipoComunicadoAtivo();
        setTiposComunicados(response.data);
      } catch (err) {
        console.error('Erro ao carregar tipos de comunicado:', err);
        // Fallback com dados estáticos em caso de erro
        setTiposComunicados([
          { id: '1', descricao: 'Tipo 1' },
          { id: '2', descricao: 'Tipo 2' },
          { id: '13', descricao: 'Tipo 13' }
        ]);
      }
    };
    
    carregarTiposComunicado();
  }, []);

  // Listar comunicados com paginação e filtros
  const listarTodosComunicados = async (page = paginaAtual) => {
    try {
      setLoading(true);
      
      // Preparar parâmetros de filtro
      const filtros = {
        nomeComunicado: nomeComunicadoFiltro || undefined,
        login: loginFiltro || undefined,
        tipoComunicado: tipoComunicadoFiltro || undefined,
        dataInicio: dataInicioFiltro ? dataInicioFiltro.toISOString() : undefined,
        dataFim: dataFimFiltro ? dataFimFiltro.toISOString() : undefined
      };
      
      const responseList = await comunicadoConfirmacaoService.listarTodosComunicadosConfirmacao(
        page, 
        tamanhoPagina, 
        filtros // Passe os filtros para o service
      );
      
      setComunicados(responseList.data.content);
      setTotalPaginas(responseList.data.totalPages);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  // Buscar confirmações de leitura
  const buscarConfirmacoesLeitura = async (uuid: string, page = 0, size = 10) => {
    try {
      setLoadingConfirmacoes(true);
      const response = await comunicadoConfirmacaoService.listarTodosComunicadosConfirmacaoUuid(uuid, page, size);
      
      setConfirmacoes(response.data.content || response.data);
      setTotalPaginasConfirmacoes(response.data.totalPages || 1);
      setLoadingConfirmacoes(false);
    } catch (err: any) {
      setLoadingConfirmacoes(false);
      console.error('Erro ao buscar confirmações:', err);
    }
  };

  // Função para aplicar filtros
  const aplicarFiltros = () => {
    setPaginaAtual(0); // Resetar para primeira página
    listarTodosComunicados(0);
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    setNomeComunicadoFiltro('');
    setLoginFiltro('');
    setTipoComunicadoFiltro('');
    setDataInicioFiltro(undefined);
    setDataFimFiltro(undefined);
    setPaginaAtual(0);
    listarTodosComunicados(0);
  };

  // Função para gerar relatório 

  const downloadFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const gerarRelatorio = async () => {
    try {
      if (!comunicadoSelecionado?.codigo) {
        alert('Nenhum comunicado selecionado para exportar');
        return;
      }

      setLoadingConfirmacoes(true);
      const response = await relatorioService.gerarRelatorioConfirmacaoComunicados(
        comunicadoSelecionado.codigo
      );
      
      // Criar blob e fazer download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const filename = `relatorio-confirmacoes-${comunicadoSelecionado.nomeComunicado}-${format(new Date(), 'dd-MM-yyyy')}.csv`;
      
      downloadFile(blob, filename);
      
    } catch (err: any) {
      console.error('Erro ao gerar relatório:', err);
      alert('Erro ao exportar relatório: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingConfirmacoes(false);
    }
  };

  // Abrir modal e carregar confirmações
  const abrirModalConfirmacoes = async (comunicado: Comunicado) => {
    setComunicadoSelecionado(comunicado);
    setShowModal(true);
    setPaginaConfirmacoesAtual(0);

    const uuid = comunicado.codigo;
    if (uuid) {
      await buscarConfirmacoesLeitura(uuid, 0, tamanhoPagina);
    } else {
      console.error('Comunicado não possui UUID válido:', comunicado);
      setConfirmacoes([]);
    }
  };

  // Fechar modal
  const fecharModal = () => {
    setShowModal(false);
    setConfirmacoes([]);
    setComunicadoSelecionado(null);
  };

  // Quando mudar página do modal de confirmações
  const mudarPaginaConfirmacoes = async (novaPagina: number) => {
    if (!comunicadoSelecionado) return;
    const uuid = comunicadoSelecionado.codigo;
    setPaginaConfirmacoesAtual(novaPagina);
    await buscarConfirmacoesLeitura(uuid, novaPagina, 5);
  };

  useEffect(() => {
    listarTodosComunicados();
  }, [refresh, paginaAtual]);

  // Renderizar botões de paginação para comunicados
  const renderizarBotoesPaginacao = () => {
    const botoes = [];
    const maxBotoes = 7;
    let inicio = Math.max(0, paginaAtual - Math.floor(maxBotoes / 2));
    let fim = inicio + maxBotoes - 1;

    if (fim >= totalPaginas) {
      fim = totalPaginas - 1;
      inicio = Math.max(0, fim - maxBotoes + 1);
    }

    if (inicio > 0) {
      botoes.push(
        <Button key={0} variant="link" onClick={() => setPaginaAtual(0)}>1</Button>
      );
      if (inicio > 1) {
        botoes.push(<span key="inicio-ellipsis" style={{ margin: '0 5px' }}>...</span>);
      }
    }

    for (let i = inicio; i <= fim; i++) {
      botoes.push(
        <Button
          key={i}
          variant={i === paginaAtual ? "primary" : "link"}
          onClick={() => setPaginaAtual(i)}
          style={{ fontWeight: i === paginaAtual ? 'bold' : 'normal' }}
        >
          {i + 1}
        </Button>
      );
    }

    if (fim < totalPaginas - 1) {
      if (fim < totalPaginas - 2) {
        botoes.push(<span key="fim-ellipsis" style={{ margin: '0 5px' }}>...</span>);
      }
      botoes.push(
        <Button key={totalPaginas - 1} variant="link" onClick={() => setPaginaAtual(totalPaginas - 1)}>
          {totalPaginas}
        </Button>
      );
    }

    return botoes;
  };

  // Renderizar botões de paginação para confirmações
  const renderizarBotoesPaginacaoConfirmacoes = () => {
    if (totalPaginasConfirmacoes <= 1) return null;
    
    const botoes = [];
    const maxBotoes = 5;
    let inicio = Math.max(0, paginaConfirmacoesAtual - Math.floor(maxBotoes / 2));
    let fim = inicio + maxBotoes - 1;

    if (fim >= totalPaginasConfirmacoes) {
      fim = totalPaginasConfirmacoes - 1;
      inicio = Math.max(0, fim - maxBotoes + 1);
    }

    if (inicio > 0) {
      botoes.push(
        <Button key={'c-0'} variant="link" onClick={() => mudarPaginaConfirmacoes(0)}>1</Button>
      );
      if (inicio > 1) {
        botoes.push(<span key="c-inicio-ellipsis" style={{ margin: '0 5px' }}>...</span>);
      }
    }

    for (let i = inicio; i <= fim; i++) {
      botoes.push(
        <Button
          key={'c-' + i}
          variant={i === paginaConfirmacoesAtual ? "primary" : "link"}
          onClick={() => mudarPaginaConfirmacoes(i)}
          style={{ fontWeight: i === paginaConfirmacoesAtual ? 'bold' : 'normal' }}
        >
          {i + 1}
        </Button>
      );
    }

    if (fim < totalPaginasConfirmacoes - 1) {
      if (fim < totalPaginasConfirmacoes - 2) {
        botoes.push(<span key="c-fim-ellipsis" style={{ margin: '0 5px' }}>...</span>);
      }
      botoes.push(
        <Button key={'c-' + (totalPaginasConfirmacoes - 1)} variant="link" onClick={() => mudarPaginaConfirmacoes(totalPaginasConfirmacoes - 1)}>
          {totalPaginasConfirmacoes}
        </Button>
      );
    }

    return botoes;
  };

  return (
    <>
      <div className="position-relative m-md-3">
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between' }}>
          <h5>Comunicados &gt; Confirmação de Leitura</h5>
        </div>
        <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
          <Button id="App-button-cor-principal-escura" onClick={() => setFiltros(!filtros)}>
            <FontAwesomeIcon icon={faFilter} style={{ marginRight: '10px' }} />
            Filtros
          </Button>
          <div>
          </div>
        </div>
        
        {filtros && (
          <>
            <Row className="mb-4" style={{ marginTop: '15px' }}>
              <Form.Group as={Col} md='4' controlId="formBasicSelect">
                <Form.Label>Nome do Comunicado</Form.Label>
                <Form.Control 
                  type="text" 
                  value={nomeComunicadoFiltro} 
                  onChange={(e) => setNomeComunicadoFiltro(e.target.value)} 
                />
              </Form.Group>
              <Form.Group as={Col} md='4' controlId="formBasicSelect">
                <Form.Label>Login</Form.Label>
                <Form.Control 
                  type="text" 
                  value={loginFiltro} 
                  onChange={(e) => setLoginFiltro(e.target.value)} 
                />
              </Form.Group>
            </Row>
            <Row className="mb-3">
              <Form.Group as={Col} md='4' controlId="formBasicSelect">
                <Form.Label>Tipo de Comunicado</Form.Label>
                <Form.Select 
                  value={tipoComunicadoFiltro} 
                  onChange={(e) => setTipoComunicadoFiltro(e.target.value)}
                >
                  <option value="">Selecione um tipo de comunicado</option>
                  {tiposComunicados.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.id + ' - ' + item.descricao}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group as={Col} md='2'>
                <Form.Label>Data de Início</Form.Label>
                <Form.Control 
                  type="date" 
                  value={dataInicioFiltro ? dataInicioFiltro.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const newDate = new Date(e.target.value);
                      newDate.setHours(newDate.getHours() + newDate.getTimezoneOffset() / 60);
                      setDataInicioFiltro(newDate);
                    } else {
                      setDataInicioFiltro(undefined);
                    }
                  }}
                />
              </Form.Group>
              <Form.Group as={Col} md='1' style={{ display: 'flex', marginTop: '30px', width: '1%' }}>
                <strong style={{ marginTop: '8px' }}>a</strong>
              </Form.Group>
              <Form.Group as={Col} md='2'>
                <Form.Label>Data de Fim</Form.Label>
                <Form.Control 
                  type="date" 
                  value={dataFimFiltro ? dataFimFiltro.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      const newDate = new Date(e.target.value);
                      newDate.setHours(newDate.getHours() + newDate.getTimezoneOffset() / 60);
                      setDataFimFiltro(newDate);
                    } else {
                      setDataFimFiltro(undefined);
                    }
                  }}
                />
              </Form.Group>
            </Row>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button variant="secondary" onClick={limparFiltros}>
                Limpar Filtros
              </Button>
              <Button id="App-button-cor-principal-escura" onClick={aplicarFiltros}>
                Aplicar Filtros
              </Button>
            </div>
          </>
        )}
          
        {loading ? <Spinner loading={loading} /> : (
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th scope="col" id="App-header-table">Código</th>
                  <th scope="col" id="App-header-table">Nome</th>
                  <th scope="col" id="App-header-table">Data Início</th>
                  <th scope="col" id="App-header-table">Data Fim</th>
                  <th scope="col" id="App-header-table">Visualizar</th>
                  <th scope="col" id="App-header-table">Confirmação</th>
                </tr>
              </thead>
              <tbody>
                {comunicados.length > 0 ? (
                  comunicados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.nomeComunicado}</td>
                      <td>{item.dtInicio ? format(new Date(item.dtInicio), 'dd/MM/yyyy') : ''}</td>
                      <td>{item.dtFim ? format(new Date(item.dtFim), 'dd/MM/yyyy') : ''}</td>
                      <td>
                        <Button
                          style={{ background: 'grey', border: 'grey', marginLeft: '5px' }}
                          onClick={() => navigate('/comunicados-visualizacao', { state: { id: item.id } })}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                      </td>
                      <td>
                        <Button
                          style={{ background: 'green', border: 'green', marginLeft: '5px' }}
                          onClick={() => abrirModalConfirmacoes(item)}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">
                      Nenhum comunicado encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalPaginas > 1 && (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Button
                  onClick={() => setPaginaAtual(prev => Math.max(prev - 1, 0))}
                  disabled={paginaAtual === 0}
                  style={{ marginRight: '10px' }}
                >
                  «
                </Button>

                {renderizarBotoesPaginacao()}

                <Button
                  onClick={() => setPaginaAtual(prev => Math.min(prev + 1, totalPaginas - 1))}
                  disabled={paginaAtual >= totalPaginas - 1}
                  style={{ marginLeft: '10px' }}
                >
                  »
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Modal de Confirmações de Leitura */}
        <Modal show={showModal} onHide={fecharModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Confirmações de Leitura - {comunicadoSelecionado?.nomeComunicado}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingConfirmacoes ? (
              <div className="text-center">
                <Spinner loading={true} />
                <p>Carregando confirmações...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <div>
                  <Button id="App-button-cor-principal-escura"
                    onClick={gerarRelatorio}
                    style={{ marginLeft: '10px' }}
                  >
                    <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: '10px' }} />
                    Exportar Relatório
                  </Button>
                </div>
                <br />
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Promotora</th>
                      <th>Origem</th>
                      <th>Usuário</th>
                      <th>Nome</th>
                      <th>Data e Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmacoes && confirmacoes.length > 0 ? (
                      confirmacoes.map((confirmacao) => (
                        <tr key={confirmacao.id}>
                          <td>{confirmacao.promotora || 'N/A'}</td>
                          <td>{confirmacao.origem || 'N/A'}</td>
                          <td>{confirmacao.login}</td>
                          <td>{confirmacao.nome}</td>
                          <td>
                            {confirmacao.dtConfirmacao
                              ? format(new Date(confirmacao.dtConfirmacao), 'dd/MM/yyyy HH:mm')
                              : 'Não confirmado'
                            }
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center">
                          Nenhuma confirmação encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Paginação modal confirmações */}
                {totalPaginasConfirmacoes > 1 && (
                  <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Button
                      onClick={() => mudarPaginaConfirmacoes(Math.max(paginaConfirmacoesAtual - 1, 0))}
                      disabled={paginaConfirmacoesAtual === 0}
                      style={{ marginRight: '10px' }}
                    >
                      «
                    </Button>

                    {renderizarBotoesPaginacaoConfirmacoes()}

                    <Button
                      onClick={() => mudarPaginaConfirmacoes(Math.min(paginaConfirmacoesAtual + 1, totalPaginasConfirmacoes - 1))}
                      disabled={paginaConfirmacoesAtual >= totalPaginasConfirmacoes - 1}
                      style={{ marginLeft: '10px' }}
                    >
                      »
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={fecharModal}>
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default ComunicadosComponent;